import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Upload, Loader2 } from "lucide-react";

export const EnrollExpertDialog = ({ industries, onEnrolled, trigger }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "", current_designation: "", company: "", industry: "",
    education: "", experience_years: "", bio: "", rate: "", specialties: "", email: "", image: "",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/experts/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image", res.data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name || !form.current_designation || !form.industry) {
      toast.error("Name, current designation and industry are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/experts", {
        name: form.name,
        current_designation: form.current_designation,
        company: form.company,
        industry: form.industry,
        education: form.education,
        experience_years: parseInt(form.experience_years) || 0,
        bio: form.bio,
        rate: form.rate || "$100 / 45 min",
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
        email: form.email,
        image: form.image,
      });
      toast.success("Application submitted! Our team will review your profile.");
      setOpen(false);
      setForm({ name: "", current_designation: "", company: "", industry: "", education: "", experience_years: "", bio: "", rate: "", specialties: "", email: "", image: "" });
      onEnrolled && onEnrolled();
    } catch (e) {
      toast.error("Could not submit enrollment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="rounded-full font-semibold" data-testid="open-enroll-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Become an Expert
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> Join as an Expert
          </DialogTitle>
          <DialogDescription>Share your expertise with visitors across any industry. Fields marked * are required.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Photo upload */}
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20 border-2 border-border">
              <AvatarImage src={form.image} alt="Expert photo" className="object-cover" />
              <AvatarFallback className="bg-secondary">{form.name?.[0] || "?"}</AvatarFallback>
            </Avatar>
            <div>
              <Label className="text-xs mb-1.5 block">Profile photo</Label>
              <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full border border-border cursor-pointer hover:bg-secondary transition-colors" data-testid="enroll-photo-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="enroll-photo-input" />
              </label>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Full name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="enroll-name" /></F>
            <F label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="enroll-email" /></F>
          </div>
          <F label="Current designation *"><Input placeholder="e.g. Head of R&D, Carbon Black" value={form.current_designation} onChange={(e) => set("current_designation", e.target.value)} data-testid="enroll-designation" /></F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Company"><Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="enroll-company" /></F>
            <F label="Industry *">
              <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                <SelectTrigger data-testid="enroll-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>
                  {industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Education / Qualification"><Input placeholder="e.g. MBA, Harvard" value={form.education} onChange={(e) => set("education", e.target.value)} data-testid="enroll-education" /></F>
            <F label="Years of experience"><Input type="number" value={form.experience_years} onChange={(e) => set("experience_years", e.target.value)} data-testid="enroll-years" /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Session rate"><Input placeholder="$120 / 45 min" value={form.rate} onChange={(e) => set("rate", e.target.value)} data-testid="enroll-rate" /></F>
            <F label="Specialties (comma separated)"><Input placeholder="M&A, Valuation" value={form.specialties} onChange={(e) => set("specialties", e.target.value)} data-testid="enroll-specialties" /></F>
          </div>
          <F label="Short bio"><Textarea rows={3} value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="enroll-bio" /></F>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={submitting || uploading} className="rounded-full w-full font-semibold" data-testid="enroll-submit-btn">
            {submitting ? "Submitting…" : "Submit Application"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);
